import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Stack,
  Alert,
  IconButton
} from "@mui/material";
import {
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  CheckCircle as ConnectedIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from "@mui/icons-material";

const ConsultationTypeList = ({
  consultationTypes = [],
  defaultName = "Default Global Settings",
  defaultDescription = "",
  defaultEmail = "",
  defaultDuration = 60,
  defaultManager = "",
  defaultIsConnected = true,
  defaultIsActive = true,
  showDefaultCard = true,
  onSelectType,
  onAddType,
  onDeleteType,
  canEdit = true
}) => {
  const totalCount = (showDefaultCard ? 1 : 0) + consultationTypes.length;

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header bar */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h5" fontWeight={700} color="#0d3559">
              Consultation Types
            </Typography>
            <Chip 
              label={`Total (${totalCount})`} 
              size="small" 
              sx={{ bgcolor: '#e2e8f0', color: '#0d3559', fontWeight: 700 }} 
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Manage multi-calendar integrations, manager assignments, and settings for each consultation type.
          </Typography>
        </Box>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddType}
            sx={{ bgcolor: '#0d3559', textTransform: 'none', borderRadius: 1.5, px: 2.5, py: 1 }}
          >
            Add Consultation Type
          </Button>
        )}
      </Stack>

      {/* Grid of Consultation Types */}
      <Grid container spacing={3}>
        {/* Global Default Settings Card */}
        {showDefaultCard && (
          <Grid item xs={12} sm={6} md={4}>
            <Card 
              variant="outlined" 
              sx={{ 
                height: '100%', 
                borderRadius: 2, 
                borderColor: '#cbd5e1',
                bgcolor: '#f8fafc',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3
                }
              }}
            >
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Typography variant="h6" fontWeight={600} color="#0d3559">
                    {defaultName}
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip label="Global Default" size="small" variant="outlined" color="default" />
                    <Chip
                      label={defaultIsActive !== false ? "Active" : "Disabled (Hidden)"}
                      size="small"
                      sx={{
                        bgcolor: defaultIsActive !== false ? '#e8f5e9' : '#ffebee',
                        color: defaultIsActive !== false ? '#2e7d32' : '#c62828',
                        fontWeight: 600
                      }}
                    />
                  </Stack>
                </Stack>

                {defaultDescription && (
                  <Typography variant="body2" color="text.secondary" mb={2} sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {defaultDescription}
                  </Typography>
                )}

                <Stack spacing={1} mb={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PersonIcon fontSize="small" sx={{ color: '#64748b' }} />
                    <Typography variant="body2" color="text.secondary">
                      Assigned Email: <strong>{defaultManager || defaultEmail || "Store Admin"}</strong>
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <EmailIcon fontSize="small" sx={{ color: '#64748b' }} />
                    <Typography variant="body2" color="text.secondary">
                      Calendar Email: <strong>{defaultEmail || "Not configured"}</strong>
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <CalendarIcon fontSize="small" sx={{ color: '#64748b' }} />
                    <Typography variant="body2" color="text.secondary">
                      Duration: <strong>{defaultDuration || 60} min</strong>
                    </Typography>
                  </Stack>
                </Stack>

                <Alert
                  severity={defaultIsConnected ? "success" : "warning"}
                  icon={defaultIsConnected ? <ConnectedIcon fontSize="inherit" /> : <WarningIcon fontSize="inherit" />}
                  sx={{ py: 0.25, px: 1, '& .MuiAlert-message': { fontSize: '0.8rem' } }}
                >
                  {defaultIsConnected ? `Connected: ${defaultEmail || "Authorized"}` : "OAuth Not Connected"}
                </Alert>
              </CardContent>
              <Box sx={{ p: 2, pt: 0 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  endIcon={<SettingsIcon />}
                  onClick={() => onSelectType(0)}
                  sx={{ textTransform: 'none', borderColor: '#0d3559', color: '#0d3559' }}
                >
                  Configure Global Settings
                </Button>
              </Box>
            </Card>
          </Grid>
        )}

        {/* Specific Consultation Types Cards */}
        {consultationTypes.map((item, idx) => {
          const type = item?.type || item;
          const originalIdx = item?.originalIdx !== undefined ? item.originalIdx : idx;
          const provider = type.calendarProvider || "google";
          const isGoogle = provider === "google";
          const isConnected = !!(type.googleOauthTokens || type.outlookOauthTokens);

          return (
            <Grid item xs={12} sm={6} md={4} key={type._id || originalIdx}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  borderColor: '#e2e8f0',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4,
                    borderColor: '#94a3b8'
                  }
                }}
              >
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                    <Typography variant="h6" fontWeight={600} color="#0d3559">
                      {type.name || `Consultation Type ${originalIdx + 1}`}
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip
                        label={isGoogle ? "Google Calendar" : "Outlook Calendar"}
                        size="small"
                        sx={{
                          bgcolor: isGoogle ? '#e8f0fe' : '#e0f2fe',
                          color: isGoogle ? '#1a73e8' : '#0284c7',
                          fontWeight: 600
                        }}
                      />
                      <Chip
                        label={type.isActive !== false ? "Active" : "Disabled (Hidden)"}
                        size="small"
                        sx={{
                          bgcolor: type.isActive !== false ? '#e8f5e9' : '#ffebee',
                          color: type.isActive !== false ? '#2e7d32' : '#c62828',
                          fontWeight: 600
                        }}
                      />
                      {canEdit && (
                        <IconButton
                          size="small"
                          color="error"
                          title="Delete Consultation Type"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Are you sure you want to delete "${type.name || 'this consultation type'}"?`)) {
                              if (onDeleteType) onDeleteType(originalIdx);
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </Stack>

                  {type.description && (
                    <Typography variant="body2" color="text.secondary" mb={2} sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {type.description}
                    </Typography>
                  )}

                  <Stack spacing={1} mb={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PersonIcon fontSize="small" sx={{ color: '#64748b' }} />
                      <Typography variant="body2" color="text.secondary">
                        Assigned Email: <strong>{type.assignedManagerName ? (type.assignedManagerEmail ? `${type.assignedManagerName} (${type.assignedManagerEmail})` : type.assignedManagerName) : (type.assignedManagerEmail || "Unassigned")}</strong>
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <EmailIcon fontSize="small" sx={{ color: '#64748b' }} />
                      <Typography variant="body2" color="text.secondary">
                        Calendar Email: <strong>{type.calendarEmail || "Not configured"}</strong>
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <CalendarIcon fontSize="small" sx={{ color: '#64748b' }} />
                      <Typography variant="body2" color="text.secondary">
                        Duration: <strong>{type.meetingDurationMinutes || 15} min</strong>
                      </Typography>
                    </Stack>
                  </Stack>

                  <Alert
                    severity={isConnected ? "success" : "warning"}
                    icon={isConnected ? <ConnectedIcon fontSize="inherit" /> : <WarningIcon fontSize="inherit" />}
                    sx={{ py: 0.25, px: 1, '& .MuiAlert-message': { fontSize: '0.8rem' } }}
                  >
                    {isConnected ? `Connected: ${type.calendarEmail || "Authorized"}` : "OAuth Not Connected"}
                  </Alert>
                </CardContent>

                <Box sx={{ p: 2, pt: 0 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    endIcon={<SettingsIcon />}
                    onClick={() => onSelectType(originalIdx + 1)}
                    sx={{ bgcolor: '#0d3559', textTransform: 'none', '&:hover': { bgcolor: '#0b2a47' } }}
                  >
                    Configure →
                  </Button>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default ConsultationTypeList;
